<template>
  <div>
    <v-navigation-drawer
      app
      permanent
      :mini-variant="false"
      mini-variant-width="112"
      width="296"
      class="modern-sidebar"
    >
      <div class="sidebar-card">

        <!-- Logo/Brand -->
        <div class="brand-section">
          <div class="brand-wrapper">
            <img src="@/assets/logo.png" alt="Alliance" class="brand-logo" />
          </div>
        </div>

        <v-divider class="sidebar-divider"></v-divider>

        <!-- Navigation Links -->
        <div class="nav-links">
          <router-link to="/feedback" class="nav-link" active-class="nav-link--active">
            <div class="nav-link-content">
              <v-icon class="nav-icon">mdi-comment-text-outline</v-icon>
              <span class="nav-text">Feedback</span>
            </div>
            <div class="nav-link-indicator"></div>
          </router-link>

          <router-link to="/news" class="nav-link" active-class="nav-link--active">
            <div class="nav-link-content">
              <v-icon class="nav-icon">mdi-newspaper-variant-outline</v-icon>
              <span class="nav-text">News</span>
            </div>
            <div class="nav-link-indicator"></div>
          </router-link>

          <router-link to="/app-version" class="nav-link" active-class="nav-link--active">
            <div class="nav-link-content">
              <v-icon class="nav-icon">mdi-cellphone-cog</v-icon>
              <span class="nav-text">App Settings</span>
            </div>
            <div class="nav-link-indicator"></div>
          </router-link>
        </div>

        <!-- User Section at Bottom -->
        <div class="user-section">
          <v-divider class="user-divider"></v-divider>

          <!-- User Profile Card -->
          <div class="user-card">
            <div class="user-profile-info">
              <v-avatar size="48" class="user-avatar">
                <div class="avatar-gradient">
                  <v-icon color="white" size="26">mdi-account</v-icon>
                </div>
              </v-avatar>
              <div class="user-details">
                <span class="user-name">Admin User</span>
                <span class="user-role">Administrator</span>
              </div>
            </div>
            
            <!-- Logout Button -->
            <v-btn
              @click="showLogoutDialog = true"
              icon
              class="logout-btn"
              title="Log Out"
            >
              <v-icon size="20">mdi-logout-variant</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- Logout Confirmation Dialog -->
    <v-dialog v-model="showLogoutDialog" max-width="420" persistent>
      <v-card class="logout-dialog">
        <v-card-title class="dialog-header">
          <div class="dialog-icon">
            <v-icon size="32" color="white">mdi-logout-variant</v-icon>
          </div>
          <span class="dialog-title">Confirm Logout</span>
        </v-card-title>

        <v-card-text class="dialog-content">
          <p class="dialog-message">Are you sure you want to log out? You'll need to sign in again to access the dashboard.</p>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-btn
            text
            large
            @click="showLogoutDialog = false"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#8B0000"
            dark
            large
            elevation="0"
            @click="confirmLogout"
            class="confirm-btn"
          >
            <v-icon left small>mdi-logout</v-icon>
            Log Out
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'NavBar',

  data: () => ({
    showLogoutDialog: false,
  }),

  methods: {
    confirmLogout() {
      this.showLogoutDialog = false;
      this.logout();
    },

    logout() {
      // Clear any stored authentication data
      localStorage.clear();
      sessionStorage.clear();

      // Navigate to login page
      this.$router.push('/');
    },
  },
};
</script>

<style lang="scss" scoped>
// Dark red palette
$red-darkest: #4a0000;
$red-dark: #5a0000;
$red-main: #7a0a0a;
$red-base: #8b0000;

// The drawer is just an invisible frame that creates the gap around the card
.modern-sidebar {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  overflow: visible !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  ::v-deep .v-navigation-drawer__border {
    display: none;
  }

  ::v-deep .v-navigation-drawer__content {
    padding: 0;
    overflow: visible;
    height: 100%;
  }
}

// The actual floating, rounded sidebar
.sidebar-card {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 0;
  background: #f8fafd;
  box-shadow: none;
  border: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  // Soft glow overlay, clipped to the rounded corners
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: transparent;
    pointer-events: none;
  }
}

.brand-section {
  position: relative;
  padding: 36px 20px 26px;
  text-align: center;

  .brand-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .brand-logo {
    width: 80px;
    height: auto;
    filter: drop-shadow(0 4px 8px rgba(139, 0, 0, 0.15));
  }
}

.sidebar-divider {
  background: linear-gradient(90deg, transparent 0%, rgba(139, 0, 0, 0.15) 50%, transparent 100%);
  border: none;
  height: 1px;
  margin: 0 20px;
}

.nav-links {
  position: relative;
  flex: 1;
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .nav-link {
    position: relative;
    display: flex;
    align-items: center;
    padding: 10px 20px;      // was 16px 20px (controls the highlight height)
    margin: 0 14px;
    border-radius: 14px;     // was 18px
    text-decoration: none;
    color: #475569;
    font-weight: 500;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(139, 0, 0, 0.08);
      border-radius: 14px;   // was 18px, keep in sync with .nav-link
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.3s ease;
    }

    .nav-link-content {
      display: flex;
      align-items: center;
      gap: 16px;
      position: relative;
      z-index: 1;
      width: 100%;
    }

    .nav-icon {
      font-size: 24px !important;
      color: inherit !important;
      transition: all 0.3s ease;
    }

    .nav-text {
      font-size: 1rem;
      letter-spacing: 0.5px;
    }

    .nav-link-indicator {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 0;
      background: $red-base;
      border-radius: 0 4px 4px 0;
      transition: height 0.3s ease;
      box-shadow: 0 0 12px rgba(139, 0, 0, 0.4);
    }

    &:hover {
      color: $red-base;
      transform: translateX(4px);

      &::before {
        transform: scaleX(1);
      }

      .nav-icon {
        transform: scale(1.15) rotate(5deg);
        color: $red-base !important;
      }

      .nav-link-indicator {
        height: 50%;         // was 70%
      }
    }

    &--active {
      background: rgba(139, 0, 0, 0.1);
      color: $red-base;
      border: 1px solid rgba(139, 0, 0, 0.2);
      box-shadow:
        0 4px 16px rgba(139, 0, 0, 0.15),
        inset 0 1px 2px rgba(139, 0, 0, 0.1);

      .nav-icon {
        transform: scale(1.1);
        filter: drop-shadow(0 2px 4px rgba(139, 0, 0, 0.3));
        color: $red-base !important;
      }

      .nav-link-indicator {
        height: 50%;         // was 80%
        background: linear-gradient(180deg, $red-base, rgba(139, 0, 0, 0.8));
      }
    }
  }
}

.user-section {
  position: relative;
  padding: 16px;

  .user-divider {
    background: linear-gradient(90deg, transparent 0%, rgba(139, 0, 0, 0.15) 50%, transparent 100%) !important;
    border: none !important;
    height: 1px !important;
    margin: 0 0 16px 0 !important;
  }

  .user-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(139, 0, 0, 0.04), rgba(139, 0, 0, 0.02));
    border: 1px solid rgba(139, 0, 0, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, transparent, $red-base, transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      background: linear-gradient(135deg, rgba(139, 0, 0, 0.08), rgba(139, 0, 0, 0.04));
      border-color: rgba(139, 0, 0, 0.15);
      box-shadow: 0 4px 16px rgba(139, 0, 0, 0.12);

      &::before {
        opacity: 1;
      }

      .logout-btn {
        opacity: 1;
        transform: translateX(0);
      }
    }
  }

  .user-profile-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  .user-avatar {
    flex-shrink: 0;
    position: relative;
    
    .avatar-gradient {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, $red-base 0%, rgba(139, 0, 0, 0.8) 100%);
      border-radius: 50%;
      box-shadow: 
        0 0 0 3px rgba(139, 0, 0, 0.1),
        0 4px 12px rgba(139, 0, 0, 0.2);
      transition: all 0.3s ease;
    }

    &:hover .avatar-gradient {
      box-shadow: 
        0 0 0 3px rgba(139, 0, 0, 0.2),
        0 6px 16px rgba(139, 0, 0, 0.3);
      transform: scale(1.05);
    }
  }

  .user-details {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;

    .user-name {
      font-weight: 600;
      color: #1e293b;
      font-size: 0.95rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .user-role {
      font-size: 0.75rem;
      color: #64748b;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .logout-btn {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(139, 0, 0, 0.08), rgba(139, 0, 0, 0.06));
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0.7;
    transform: translateX(-4px);

    .v-icon {
      color: $red-base !important;
      transition: all 0.3s ease;
    }

    &:hover {
      background: linear-gradient(135deg, $red-base, rgba(139, 0, 0, 0.9));
      box-shadow: 0 4px 12px rgba(139, 0, 0, 0.3);
      transform: translateX(0) scale(1.05);

      .v-icon {
        color: white !important;
        transform: rotate(-10deg);
      }
    }

    &:active {
      transform: translateX(0) scale(0.95);
    }
  }
}

// Animations
@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

// Logout Dialog Styles
.logout-dialog {
  border-radius: 20px !important;
  overflow: hidden;

  .dialog-header {
    background: linear-gradient(135deg, $red-base, $red-dark);
    color: white;
    padding: 24px 28px;
    display: flex;
    align-items: center;
    gap: 16px;

    .dialog-icon {
      width: 56px;
      height: 56px;
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.15);
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    .dialog-title {
      font-size: 1.35rem;
      font-weight: 700;
      letter-spacing: -0.02em;
    }
  }

  .dialog-content {
    padding: 28px;

    .dialog-message {
      font-size: 1rem;
      line-height: 1.6;
      color: #475569;
      margin: 0;
    }
  }

  .dialog-actions {
    padding: 16px 28px 24px;
    
    .cancel-btn {
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0.3px;
      color: #64748b;
      border-radius: 10px;
      
      &:hover {
        background: rgba(100, 116, 139, 0.08);
      }
    }

    .confirm-btn {
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0.5px;
      border-radius: 12px;
      background: linear-gradient(135deg, $red-base, $red-dark) !important;
      box-shadow: 0 4px 12px rgba(139, 0, 0, 0.3) !important;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 6px 20px rgba(139, 0, 0, 0.4) !important;
        transform: translateY(-2px);
      }
    }
  }
}

// Responsive design
@media (max-width: 960px) {
  .modern-sidebar {
    ::v-deep .v-navigation-drawer__content {
      overflow-x: hidden;
    }
  }
}
</style>